<script setup lang="ts">
/**
 * Interactive tutorials — the Welcome card (handoff README §1).
 *
 * Offered once, on the Events index, before anything is running. Both buttons
 * are terminal for the card: Start begins the tour, Skip puts it away for good.
 * Which of those the parent turns into a server call is the parent's business.
 */

import { computed } from 'vue'
import TourModal from './TourModal.vue'
import { renderMarkdownLite } from './engine'
import type { Tour } from './registry'

const props = defineProps<{
  tour: Tour
  /** Shown in the eyebrow; omitted when the page didn't supply one. */
  firstName?: string
  /**
   * Set when the viewport is too narrow to run the tour. The card then offers
   * only Skip, with the "come back on a laptop" line — rather than starting a
   * run whose card would be docked from the first checkpoint.
   */
  viewportTooNarrow?: boolean
}>()

const emit = defineEmits<{
  (e: 'start'): void
  (e: 'skip'): void
}>()

const eyebrow = computed(() => (props.firstName ? `Welcome, ${props.firstName}` : 'Welcome'))

const bodyHtml = computed(() =>
  renderMarkdownLite(
    "We'll walk you through it on a real event of yours — **the event**, **the pricing basis**, " +
      '**the arrangements**, and **sending it for signature**. We point to each step as you go.'
  )
)
</script>

<template>
  <TourModal
    :eyebrow="eyebrow"
    title="Build your first quote"
    :steps="tour.steps"
    :current-step="0"
  >
    <!-- eslint-disable-next-line vue/no-v-html -->
    <p class="tour-modal__body" v-html="bodyHtml" />
    <p v-if="viewportTooNarrow" class="tour-modal__meta">
      Open BLUME on a laptop to take the tour — it needs the room.
    </p>

    <template #foot>
      <button
        v-if="!viewportTooNarrow"
        type="button"
        class="bz-btn bz-btn-primary"
        @click="emit('start')"
      >
        Start
      </button>
      <button type="button" class="tour-link" @click="emit('skip')">
        Skip — find it later under Tutorials
      </button>
    </template>
  </TourModal>
</template>
